import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Button, Modal, Form, Input, Switch, Space, Popconfirm } from 'antd';
import { DatabaseOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;

const DataSources = () => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDataSource, setEditingDataSource] = useState(null);
  const [deletingDataSource, setDeletingDataSource] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchDataSources();
  }, []);

  useEffect(() => {
    fetchDataSources();
  }, [pagination.current, pagination.pageSize]);

  const fetchDataSources = async () => {
    try {
      const response = await tradeAPI.getDataSources();
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setDataSources(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取数据源失败');
      console.error('获取数据源失败:', error);
      setDataSources([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const handleCreate = () => {
    setEditingDataSource(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDataSource(record);
    form.setFieldsValue({
      name: record.name,
      base_url: record.base_url,
      enabled: record.enabled,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingDataSource(record);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDataSource) return;

    try {
      await tradeAPI.deleteDataSource(deletingDataSource.id);
      message.success('数据源删除成功');
      setIsDeleteModalVisible(false);
      setDeletingDataSource(null);
      fetchDataSources();
    } catch (error) {
      message.error('删除数据源失败');
      console.error('删除数据源失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingDataSource(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingDataSource) {
        // 更新数据源
        await tradeAPI.updateDataSource(editingDataSource.id, values);
        message.success('数据源更新成功');
      } else {
        // 创建数据源
        await tradeAPI.createDataSource(values);
        message.success('数据源创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchDataSources();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '基础URL',
      dataIndex: 'base_url',
      key: 'base_url',
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => <Tag color={enabled ? 'green' : 'red'}>{enabled ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除" description={`确定要删除数据源 "${record.name}" 吗？`} onConfirm={() => handleConfirmDelete(record)} okText="确认删除" cancelText="取消" okType="danger">
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>数据源管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建数据源
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={dataSources}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title={editingDataSource ? '编辑数据源' : '新建数据源'} open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="数据源名称" rules={[{ required: true, message: '请输入数据源名称' }]}>
            <Input placeholder="请输入数据源名称" />
          </Form.Item>

          <Form.Item name="base_url" label="基础URL">
            <Input placeholder="请输入基础URL (可选)" />
          </Form.Item>

          <Form.Item name="enabled" label="启用状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除数据源 "{deletingDataSource?.name}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该数据源及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default DataSources;
